import React, { useCallback, useEffect, useState } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import AttributeRenderer from "../common/AttributeRenderer";
import NumberAndRangeElement from "../seviceBuilder/NumberAndRangeElement";
import { enReportServiceType } from "../seviceBuilder/helper";
import StartServiceElement from "./StartServiceElement";
import { Box, createTheme, ThemeProvider } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { enUS } from "date-fns/locale";
import CustomTooltip from "../common/CustomTooltip";
import InfoIcon from "../../svg/InfoIcon";

interface ServiceEditorProps {
  service: any;
  onServiceChange: (updatedService: any) => void;
  forceSmallScreen?: boolean;
  type?: string;
}

const ServiceEditor = ({
  service,
  onServiceChange,
  forceSmallScreen = false,
  type = "logicBuilder",
}: ServiceEditorProps) => {
  const theme: Theme = useTheme();
  const [isSmallScreen, setIsSmallScreen] = useState(
    forceSmallScreen || window.innerWidth < 1100
  );
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  // Calculate the unitValue that will be used
  const calculatedUnitValue =
    service.totalDurationUnit &&
    service.totalDurationUnit.trim() !== "" &&
    service.totalDurationUnit.trim() !== "None"
      ? service.totalDurationUnit.trim()
      : "months";

  // Initialize startDate from service when component mounts or service changes
  useEffect(() => {
    if (service.startDate) {
      setStartDate(new Date(service.startDate));
    }
  }, [service.startDate]);

  // Initialize endDate from service when component mounts or service changes
  useEffect(() => {
    if (service.endDate) {
      setEndDate(new Date(service.endDate));
    }
  }, [service.endDate]);

  // Handle window resize for responsive layout (only if not forced)
  useEffect(() => {
    if (forceSmallScreen) {
      setIsSmallScreen(true);
      return;
    }

    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1100);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [forceSmallScreen]);

  const handleAttributeChange = useCallback(
    (updatedAttribute: any) => {
      const updatedService = {
        ...service,
        attributes: service.attributes.map((attr: any) =>
          (attr.id || attr.attributeID) ===
          (updatedAttribute.id || updatedAttribute.attributeID)
            ? updatedAttribute
            : attr
        ),
      };
      onServiceChange(updatedService);
    },
    [service, onServiceChange]
  );

  const handleDurationChange = useCallback(
    (updatedAttribute: any) => {
      const updatedService = {
        ...service,
        totalDuration: updatedAttribute.value,
        totalDurationMin: updatedAttribute.minValue,
        totalDurationMax: updatedAttribute.maxValue,
        totalDurationUnit: updatedAttribute.unitValue,
      };
      onServiceChange(updatedService);
    },
    [service, onServiceChange]
  );

  const handleEndDateChange = useCallback(
    (newDate: Date | null) => {
      setEndDate(newDate);

      // Convert date to YYYY-MM-DD format to avoid timezone issues
      let dateString = null;
      if (newDate) {
        const year = newDate.getFullYear();
        const month = String(newDate.getMonth() + 1).padStart(2, "0");
        const day = String(newDate.getDate()).padStart(2, "0");
        dateString = `${year}-${month}-${day}`;
      }

      const updatedService = {
        ...service,
        endDate: dateString,
      };

      onServiceChange(updatedService);
    },
    [service, onServiceChange]
  );

  // Create theme to match TextInputField styling
  const customTheme = createTheme({
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              height: "56px",
              borderRadius: "8px",
              backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
              fontFamily: "var(--font-Medium)",
              fontSize: "12px",
              lineHeight: "20px",
              padding: "0 12px",
              "& input": {
                padding: "16px 0",
                transition: "padding 0.2s ease-in-out",
                fontFamily: "var(--font-Medium)",
                fontSize: "12px",
                lineHeight: "20px",
              },
              "& .MuiInputAdornment-root": {
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                margin: 0,
              },
              "& fieldset": {
                borderColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
                top: 0,
              },
              "&:hover fieldset": {
                borderColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.colors_ds.PRIMARY_ICON_MAIN,
                borderWidth: "1px",
              },
            },
            "& .MuiInputLabel-root": {
              fontFamily: "var(--font-Medium)",
              fontSize: "10px",
              backgroundColor: "transparent",
              transition: "transform 0.2s ease-in-out",
              color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
              "&.Mui-focused": {
                color: theme.colors_ds.PRIMARY_ICON_MAIN,
                transform: "translate(12px, 6px) scale(1)",
              },
            },
            "& legend": {
              display: "none",
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: "8px",
            boxShadow: "var(--box-shadow-general)",
            marginTop: "8px",
            border: `1px solid ${theme.colors_ds.PRIMARY_ICON_MAIN}`,
          },
        },
      },
      // @ts-ignore
      MuiPickersCalendarHeader: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
          },
          label: {
            fontFamily: "var(--font-Bold)",
            fontSize: "14px",
            color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            fontFamily: "var(--font-Medium)",
            fontSize: "12px",
            "&[aria-selected='true']": {
              backgroundColor: theme.colors_ds.PRIMARY_ICON_MAIN,
              color: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
              fontFamily: "var(--font-Bold)",
              "&:hover": {
                backgroundColor: theme.colors_ds.PRIMARY_ICON_MAIN,
              },
            },
            "&:hover": {
              backgroundColor: theme.colorTokens.N[40],
            },
          },
          today: {
            borderColor: "transparent",
            '&:not([aria-selected="true"])': {
              borderColor: theme.colors_ds.PRIMARY_ICON_MAIN,
            },
          },
        },
      },
      MuiDayCalendar: {
        styleOverrides: {
          weekDayLabel: {
            fontFamily: "var(--font-Bold)",
            fontSize: "12px",
            color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
          },
        },
      },
    },
  });

  return (
    <div style={styles.container(theme)}>
      <div style={styles.content}>
        {/* Service header */}
        <div style={styles.serviceHeader}>
          <div style={styles.serviceTitle(theme)}>{service.name}</div>
          <div style={styles.categoryName(theme)}>
            {service.serviceType
              ? enReportServiceType[service.serviceType]
              : "No type specified"}
          </div>
        </div>

        {/* Attributes grid */}
        <div style={styles.attributesContainer(isSmallScreen)}>
          <StartServiceElement
            service={service}
            onServiceChange={onServiceChange}
            type={type}
          />
          {/* End Date field */}
          {service.serviceType !== 7 &&
            service.serviceType !== 6 &&
            type === "prescriptionBuilder" && (
              <Box>
                <Box sx={styles.headerContainer}>
                  <Box style={styles.title(theme)}>End date</Box>
                  <CustomTooltip
                    message={
                      "The end date until which the service will be prescribed."
                    }
                  >
                    <InfoIcon
                      color={theme.colors_ds.NEUTRAL_ICON_WEAK}
                      size={16}
                    />
                  </CustomTooltip>
                </Box>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={{ ...enUS, options: { weekStartsOn: 1 } }}
                >
                  <ThemeProvider theme={customTheme}>
                    <DatePicker
                      label=""
                      value={endDate}
                      onChange={handleEndDateChange}
                      format="MMM dd yyyy"
                      minDate={startDate || new Date()}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          placeholder: "MMM DD YYYY",
                          inputProps: {
                            readOnly: true,
                          },
                        },
                      }}
                    />
                  </ThemeProvider>
                </LocalizationProvider>
              </Box>
            )}
          {/* Total Duration field */}
          {service.serviceType !== 7 &&
            service.serviceType !== 6 &&
            type === "logicBuilder" && (
              <NumberAndRangeElement
                attribute={{
                  id: `duration-${service.id}`,
                  userID: "",
                  reportSvcID: "",
                  reportSvcAttributeID: "",
                  attributeID: `duration-${service.id}`,
                  userServiceID: "",
                  name:
                    service.serviceType === 3
                      ? "Total Duration of Supplementation"
                      : service.serviceType === 4
                      ? "Total Duration of Administration"
                      : "Total Treatment Duration",
                  type: 1, // Range type
                  value: service.totalDuration || 0,
                  minValue: service.totalDurationMin || 0,
                  maxValue: service.totalDurationMax || 0,
                  unitValue: calculatedUnitValue,
                  unitList: ["days", "weeks", "months", "years"],
                  defaultUnit: "months",
                  min: "0",
                  max: "0",
                  text: "",
                  textValue: "",
                  unit: "",
                  timeUnit: 0,
                  periodUnit: 0,
                  list: [],
                }}
                onAttributeChange={handleDurationChange}
              />
            )}

          {/* Render service attributes */}
          {service.attributes &&
            service.attributes.length > 0 &&
            [...service.attributes]
              .sort((a: any, b: any) => {
                // Define priority order: "Sessions Frequency" first, then "IV Sessions Frequency", then others
                const getPriority = (name: string) => {
                  if (name === "Sessions Frequency") return 0;
                  if (name === "IV Sessions Frequency") return 1;
                  if (name === "Follow-up Frequency") return 2;
                  if (name === "Administration Frequency") return 3;
                  return 4;
                };
                return getPriority(a.name) - getPriority(b.name);
              })
              .map((attribute: any, attrIndex: number) => (
                <AttributeRenderer
                  key={attribute.id || attribute.attributeID || attrIndex}
                  attribute={attribute}
                  onAttributeChange={handleAttributeChange}
                />
              ))}
        </div>
      </div>
    </div>
  );
};

export default ServiceEditor;

const styles = {
  container: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
  }),
  content: {
    padding: "16px",
  },
  serviceHeader: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
    marginBottom: "24px",
  },
  serviceTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  categoryName: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  attributesContainer: (isSmallScreen: boolean) => ({
    display: "grid",
    gridTemplateColumns: isSmallScreen ? "1fr" : "repeat(2, 1fr)",
    gap: "16px",
  }),
  headerContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "8px",
    marginBottom: "8px",
  },
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
};
